import {
  useNetworkDashboardKpis,
  useNetworkDashboardLiveStoreCa,
  useRealEstateKpis,
  useStoreProjects,
} from '@gozoki/api'
import {
  DEPARTMENT_NAME_BY_CODE,
  NETWORK_DASHBOARD_COHORT_UNITS,
  NETWORK_DASHBOARD_STORE_AGE_FILTERS,
  type NetworkDashboardAvailableStore,
  type NetworkDashboardCaDailyAggregate,
  type NetworkDashboardCohortUnit,
  type NetworkDashboardDailyAggregate,
  type NetworkDashboardMergedDailyAggregate,
  type NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDownIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useState, type JSX } from 'react'
import { z } from 'zod'
import { OpenStoresCountCardLink } from '~/components/dashboards/open-stores-count-card'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { getPercentageOfAnnualCa } from '~/components/routes/development/real-estate/map/map-dashboard-stats'
import {
  computeAverageLandCost,
  computeAverageRent,
} from '~/components/routes/development/real-estate/map/map-kpis'
import type { RealEstateProject } from '~/components/routes/development/real-estate/store-project/types'
import {
  CACard,
  type NetworkDashboardCACardDateWindows,
} from '~/components/routes/network-dashboard/cards/ca-card'
import { CADetailsCard } from '~/components/routes/network-dashboard/cards/ca-details-card'
import { CategoriesCard } from '~/components/routes/network-dashboard/cards/categories-card'
import { CustomerSatifsactionCard } from '~/components/routes/network-dashboard/cards/customer-satifsaction-card'
import { HRCard } from '~/components/routes/network-dashboard/cards/hr-card'
import { NetworkStatCard } from '~/components/routes/network-dashboard/cards/network-stat-card'
import { ProductsCard } from '~/components/routes/network-dashboard/cards/products-card'
import { RealEstateCostsCard } from '~/components/routes/network-dashboard/cards/real-estate-costs-card'
import { UpcomingStoresCard } from '~/components/routes/network-dashboard/cards/upcoming-stores-card'
import {
  CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS,
  CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS,
  CaModal,
} from '~/components/routes/network-dashboard/modals/ca-modal'
import {
  getDefaultDateRange,
  NETWORK_DASHBOARD_ALL_TIME_START_ISO,
  NetworkFilters,
  partitionStoresByOpeningMaturity,
  type NetworkDashboardComparableSelectionMode,
  type NetworkFiltersRowSelection,
} from '~/components/routes/network-dashboard/network-filters'
import { SelectionPeriodBadge } from '~/components/routes/network-dashboard/selection-period-badge'
import { Button } from '~/components/ui/button'
import { getUpcomingStores } from '~/utils/new-dashboards/upcoming-stores'

const DASHBOARD_BASE_TITLE = 'Réseau & Boutiques'

function filterAggregatesInRange(
  aggregates: NetworkDashboardDailyAggregate[],
  start: string,
  end: string,
): NetworkDashboardDailyAggregate[] {
  return aggregates.filter((a) => a.date >= start && a.date <= end)
}

function filterCaAggregatesInRange(
  rows: NetworkDashboardCaDailyAggregate[],
  start: string,
  end: string,
): NetworkDashboardCaDailyAggregate[] {
  return rows.filter((a) => a.date >= start && a.date <= end)
}

function mergeOperationalAndCaForCaCard(
  operational: NetworkDashboardDailyAggregate[],
  caRows: NetworkDashboardCaDailyAggregate[],
): NetworkDashboardMergedDailyAggregate[] {
  const caByDate = new Map(caRows.map((r) => [r.date, r]))
  const opDates = new Set(operational.map((r) => r.date))

  const mergedFromOperational: NetworkDashboardMergedDailyAggregate[] = operational.map((row) => {
    const ca = caByDate.get(row.date)
    if (!ca) {
      return { ...row, totalCa: 0, matureCa: 0 }
    }
    return {
      ...row,
      totalCa: ca.totalCa,
      matureCa: ca.matureCa,
      storeCount: ca.storeCount,
      matureStoreCount: ca.matureStoreCount,
    }
  })

  const extras: NetworkDashboardMergedDailyAggregate[] = caRows
    .filter((r) => !opDates.has(r.date))
    .map((ca) => ({
      date: ca.date,
      totalCaHt: 0,
      totalBuyingPrice: 0,
      totalProductsSold: 0,
      totalCartsCount: 0,
      totalStolenCount: 0,
      totalStolenBuyingValue: 0,
      totalDestroyedCount: 0,
      totalDestroyedBuyingValue: 0,
      storeCount: ca.storeCount,
      matureStoreCount: ca.matureStoreCount,
      matureCaHt: 0,
      matureBuyingPrice: 0,
      matureProductsSold: 0,
      matureCartsCount: 0,
      matureStolenCount: 0,
      matureStolenBuyingValue: 0,
      matureDestroyedCount: 0,
      matureDestroyedBuyingValue: 0,
      totalCaByHour: Array.from({ length: 24 }, () => 0),
      totalCartsByHour: Array.from({ length: 24 }, () => 0),
      totalArticlesSoldByHour: Array.from({ length: 24 }, () => 0),
      matureCaByHour: Array.from({ length: 24 }, () => 0),
      matureCartsByHour: Array.from({ length: 24 }, () => 0),
      matureArticlesSoldByHour: Array.from({ length: 24 }, () => 0),
      totalCa: ca.totalCa,
      matureCa: ca.matureCa,
    }))

  return [...mergedFromOperational, ...extras].sort((a, b) => a.date.localeCompare(b.date))
}

function sumCaRows(rows: NetworkDashboardCaDailyAggregate[], useMatureOnly: boolean): number {
  return rows.reduce((sum, r) => sum + (useMatureOnly ? r.matureCa : r.totalCa), 0)
}

function annualizePeriodValue(value: number, startIso: string, endIso: string): number {
  const start = DateTime.fromISO(startIso).startOf('day')
  const end = DateTime.fromISO(endIso).startOf('day')
  const days = Math.floor(end.diff(start, 'days').days) + 1
  return days > 0 ? value * (365 / days) : 0
}

function formatPercentValue(value: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(value)}%`
}

function resolveCaModalShowMatureOnly(
  variant: 'network' | 'store',
  ageFilter: NetworkDashboardStoreAgeFilter,
): boolean {
  if (variant === 'network') {
    return false
  }
  return ageFilter === 'default'
}

function ageFilterDisplayLabel(filter: NetworkDashboardStoreAgeFilter): string | null {
  if (filter === 'less_than_3_months') return '< 3 mois'
  if (filter === 'between_3_and_6_months') return '3-6 mois'
  if (filter === 'between_6_and_12_months') return '6-12 mois'
  if (filter === 'more_than_12_months') return '> 12 mois'
  return null
}

function resolveSelectedDepartmentLabel(
  availableStores: NetworkDashboardAvailableStore[],
  selectedStoreIds: number[],
): string | null {
  if (selectedStoreIds.length === 0) {
    return null
  }
  const selectedIds = new Set(selectedStoreIds)
  const selectedStores = availableStores.filter((store) => selectedIds.has(store.id))
  if (selectedStores.length !== selectedStoreIds.length) {
    return null
  }
  const departmentCode = selectedStores[0]?.departmentCode ?? null
  if (selectedStores.some((store) => store.departmentCode !== departmentCode)) {
    return null
  }
  const departmentStores = availableStores.filter(
    (store) => store.departmentCode === departmentCode,
  )
  if (departmentStores.length !== selectedStores.length) {
    return null
  }
  const isExactDepartmentSelection = departmentStores.every((store) => selectedIds.has(store.id))
  if (!isExactDepartmentSelection) {
    return null
  }
  if (departmentCode === null) {
    return 'Sans département'
  }
  return DEPARTMENT_NAME_BY_CODE[departmentCode] ?? departmentCode
}

function buildSelectionContextTitle(params: {
  availableStores: NetworkDashboardAvailableStore[]
  storeIds: number[]
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  ageStoreIds?: number[]
}): string {
  const ageLabel = ageFilterDisplayLabel(params.storeAgeFilter)
  const hasAgeSelection = ageLabel !== null || (params.ageStoreIds?.length ?? 0) > 0
  const hasStoreSelection = params.storeIds.length > 0
  const hasMultipleFilterKinds = hasAgeSelection && hasStoreSelection

  if (hasMultipleFilterKinds) {
    return `${DASHBOARD_BASE_TITLE} - Sélection`
  }

  if (hasStoreSelection && params.storeIds.length === 1) {
    const selectedStore = params.availableStores.find((store) => store.id === params.storeIds[0])
    return `Boutique - ${selectedStore?.label ?? String(params.storeIds[0])}`
  }

  if (hasStoreSelection) {
    const departmentLabel = resolveSelectedDepartmentLabel(params.availableStores, params.storeIds)
    if (departmentLabel !== null) {
      return `${DASHBOARD_BASE_TITLE} - ${departmentLabel}`
    }
    return `${DASHBOARD_BASE_TITLE} - Sélection`
  }

  if (ageLabel !== null) {
    return `${DASHBOARD_BASE_TITLE} ${ageLabel}`
  }

  if ((params.ageStoreIds?.length ?? 0) > 0) {
    return `${DASHBOARD_BASE_TITLE} - Sélection`
  }

  return DASHBOARD_BASE_TITLE
}

function computePercentageChange(current: number, previous: number): number {
  if (previous === 0) {
    return 0
  }
  return Math.round(((current - previous) / previous) * 100)
}

function resolveSelectedStoresForPeriod(params: {
  availableStores: NetworkDashboardAvailableStore[]
  storeIds: number[]
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  ageStoreIds?: number[]
  periodEnd: string
}): NetworkDashboardAvailableStore[] {
  const geographicStoreIds = params.storeIds.length > 0 ? new Set(params.storeIds) : null
  const ageStoreIds =
    params.ageStoreIds !== undefined
      ? new Set(params.ageStoreIds)
      : resolveStoreAgePresetIds(params.availableStores, params.storeAgeFilter, params.periodEnd)

  return params.availableStores.filter((store) => {
    if (geographicStoreIds !== null && !geographicStoreIds.has(store.id)) {
      return false
    }
    if (ageStoreIds !== null && !ageStoreIds.has(store.id)) {
      return false
    }
    return true
  })
}

function resolveStoreAgePresetIds(
  availableStores: NetworkDashboardAvailableStore[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter,
  periodEnd: string,
): Set<number> | null {
  if (storeAgeFilter === 'default' || storeAgeFilter === 'all') {
    return null
  }

  const partition = partitionStoresByOpeningMaturity(availableStores, periodEnd)
  const stores =
    storeAgeFilter === 'less_than_3_months'
      ? partition.lessThan3
      : storeAgeFilter === 'between_3_and_6_months'
        ? partition.between3And6
        : storeAgeFilter === 'between_6_and_12_months'
          ? partition.between6And12
          : partition.moreThan12

  return new Set(stores.map((store) => store.id))
}

function countOpenStoresAtPeriodEnd(
  stores: NetworkDashboardAvailableStore[],
  periodEnd: string,
): number {
  return stores.filter((store) => store.openingDate <= periodEnd).length
}

function formatNetworkDashboardComparisonPeriodFr(start: string, end: string): string {
  const startISO = DateTime.fromISO(start)
  const endISO = DateTime.fromISO(end)
  if (start === end) {
    return 'J'
  }
  if (startISO.plus({ days: 6 }).equals(endISO)) {
    return 'S'
  }
  if (startISO.plus({ days: 29 }).equals(endISO)) {
    return 'M'
  }
  if (startISO.plus({ days: 364 }).equals(endISO)) {
    return 'A'
  }
  return 'C'
}

function formatCohortBadgeLabel(
  unit: NetworkDashboardCohortUnit | undefined,
  value: number | undefined,
): string {
  if (unit === undefined || value === undefined) {
    return 'Cohorte'
  }
  const unitLabel: Record<NetworkDashboardCohortUnit, string> = {
    day: value === 1 ? 'jour' : 'jours',
    week: value === 1 ? 'semaine' : 'semaines',
    month: 'mois',
    year: value === 1 ? 'année' : 'années',
  }
  return `Cohorte ${value} ${unitLabel[unit]}`
}

function buildAutoCalendarComparisonRangeForCohort(
  unit: NetworkDashboardCohortUnit,
  value: number,
): { startDate: string; endDate: string } {
  const end = DateTime.now().setZone('Europe/Paris').startOf('day').minus({ days: 1 })
  const start =
    unit === 'day'
      ? end.minus({ days: value - 1 })
      : unit === 'week'
        ? end.minus({ weeks: value }).plus({ days: 1 })
        : unit === 'month'
          ? end.minus({ months: value }).plus({ days: 1 })
          : end.minus({ years: value }).plus({ days: 1 })

  return { startDate: start.toISODate()!, endDate: end.toISODate()! }
}

function buildNetworkKpisPeriodForSelection(params: {
  selection: NetworkFiltersRowSelection | null
  fallbackStartDate: string
  fallbackEndDate: string
  fallbackAgeStoreIds?: number[]
  fallbackCohortUnit?: NetworkDashboardCohortUnit
  fallbackCohortValue?: number
}): {
  startDate?: string
  endDate?: string
  activeStartDate?: string
  activeEndDate?: string
  cohortUnit?: NetworkDashboardCohortUnit
  cohortValue?: number
  ageStoreIds?: number[]
} {
  const { selection, fallbackStartDate, fallbackEndDate } = params

  if (selection) {
    const ageStoreIds =
      selection.ageStoreIds !== undefined ? { ageStoreIds: selection.ageStoreIds } : {}

    if (selection.cohort?.enabled === true) {
      return {
        cohortUnit: selection.cohort.unit,
        cohortValue: selection.cohort.value,
        ...ageStoreIds,
      }
    }

    return {
      startDate: selection.dateRange.from
        ? DateTime.fromJSDate(selection.dateRange.from).toISODate()!
        : NETWORK_DASHBOARD_ALL_TIME_START_ISO,
      endDate: DateTime.fromJSDate(selection.dateRange.to).toISODate()!,
      ...(params.fallbackCohortUnit === undefined
        ? { activeStartDate: fallbackStartDate, activeEndDate: fallbackEndDate }
        : {}),
      ...ageStoreIds,
    }
  }

  return {
    ...(params.fallbackCohortUnit !== undefined && params.fallbackCohortValue !== undefined
      ? { cohortUnit: params.fallbackCohortUnit, cohortValue: params.fallbackCohortValue }
      : { startDate: fallbackStartDate, endDate: fallbackEndDate }),
    ...(params.fallbackAgeStoreIds !== undefined
      ? { ageStoreIds: params.fallbackAgeStoreIds }
      : {}),
  }
}

function getAggregateValue(
  aggregate: NetworkDashboardDailyAggregate,
  showMatureOnly: boolean,
  matureKey:
    | 'matureCartsCount'
    | 'matureProductsSold'
    | 'matureCaHt'
    | 'matureStolenBuyingValue'
    | 'matureStolenCount'
    | 'matureDestroyedBuyingValue'
    | 'matureDestroyedCount',
  totalKey:
    | 'totalCartsCount'
    | 'totalProductsSold'
    | 'totalCaHt'
    | 'totalStolenBuyingValue'
    | 'totalStolenCount'
    | 'totalDestroyedBuyingValue'
    | 'totalDestroyedCount',
): number {
  if (showMatureOnly) {
    return aggregate[matureKey]
  }

  return aggregate[totalKey]
}

function NetworkDashboardPage(): JSX.Element {
  const {
    storeIds,
    storeAgeFilter,
    ageStoreIds,
    startDate,
    endDate,
    cohortUnit,
    cohortValue,
    comparableSelectionMode,
    comparableStoreIds,
    comparableStoreAgeFilter,
    comparableAgeStoreIds,
    comparableStartDate,
    comparableEndDate,
    comparableCohortUnit,
    comparableCohortValue,
  } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: projects = [] } = useStoreProjects()
  const { data: realEstateKpis } = useRealEstateKpis({ openStoresComparisonDays: 7 })

  const defaultRange = getDefaultDateRange()
  const requestStartDate =
    startDate ??
    (defaultRange.from
      ? DateTime.fromJSDate(defaultRange.from).toISODate()!
      : NETWORK_DASHBOARD_ALL_TIME_START_ISO)
  const requestEndDate = endDate ?? DateTime.fromJSDate(defaultRange.to).toISODate()!

  const isCohortMode = cohortUnit !== undefined && cohortValue !== undefined
  const effectiveComparableSelectionMode: NetworkDashboardComparableSelectionMode =
    comparableSelectionMode
  const isComparableCohortMode =
    comparableCohortUnit !== undefined && comparableCohortValue !== undefined
  const autoCohortComparableRange =
    isCohortMode && effectiveComparableSelectionMode === 'auto'
      ? buildAutoCalendarComparisonRangeForCohort(cohortUnit, cohortValue)
      : undefined

  const { data: networkKpis } = useNetworkDashboardKpis(storeIds, storeAgeFilter, {
    ...(isCohortMode
      ? { cohortUnit, cohortValue }
      : { startDate: requestStartDate, endDate: requestEndDate }),
    ...(ageStoreIds !== undefined ? { ageStoreIds } : {}),
  })

  const { data: liveStoreCa } = useNetworkDashboardLiveStoreCa()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCaModalOpen, setIsCaModalOpen] = useState(false)
  const [caModalVariant, setCaModalVariant] = useState<'network' | 'store'>('network')
  const [reopenCaModalAfterFiltersClose, setReopenCaModalAfterFiltersClose] = useState(false)
  const [selectorSource, setSelectorSource] = useState<'header' | 'caModal'>('header')
  const [caDetailIndicatorIds, setCaDetailIndicatorIds] = useState<{
    principal: string
    comparable: string
  } | null>(null)
  const [caDetailPrincipalSelection, setCaDetailPrincipalSelection] =
    useState<NetworkFiltersRowSelection | null>(null)
  const [caDetailComparableSelection, setCaDetailComparableSelection] =
    useState<NetworkFiltersRowSelection | null>(null)

  const isMainCustomComparable = effectiveComparableSelectionMode === 'custom'
  const mainComparableSelection: NetworkFiltersRowSelection | null =
    isMainCustomComparable || autoCohortComparableRange
      ? {
          storeIds: isMainCustomComparable ? comparableStoreIds : storeIds,
          storeAgeFilter: isMainCustomComparable ? comparableStoreAgeFilter : storeAgeFilter,
          ageStoreIds: isMainCustomComparable ? comparableAgeStoreIds : ageStoreIds,
          dateRange: {
            from:
              (comparableStartDate ?? autoCohortComparableRange?.startDate ?? requestStartDate) ===
              NETWORK_DASHBOARD_ALL_TIME_START_ISO
                ? null
                : DateTime.fromISO(
                    comparableStartDate ?? autoCohortComparableRange?.startDate ?? requestStartDate,
                  ).toJSDate(),
            to: DateTime.fromISO(
              comparableEndDate ?? autoCohortComparableRange?.endDate ?? requestEndDate,
            ).toJSDate(),
          },
          cohort: {
            enabled: isMainCustomComparable && isComparableCohortMode,
            unit: comparableCohortUnit ?? 'week',
            value: comparableCohortValue ?? 1,
          },
        }
      : null
  const mainComparablePeriod = buildNetworkKpisPeriodForSelection({
    selection: mainComparableSelection,
    fallbackStartDate: requestStartDate,
    fallbackEndDate: requestEndDate,
    fallbackAgeStoreIds: ageStoreIds,
    fallbackCohortUnit: cohortUnit,
    fallbackCohortValue: cohortValue,
  })
  const { data: mainComparableNetworkKpis } = useNetworkDashboardKpis(
    mainComparableSelection?.storeIds ?? storeIds,
    mainComparableSelection?.storeAgeFilter ?? storeAgeFilter,
    mainComparablePeriod,
  )

  const caModalPrincipalPeriod = buildNetworkKpisPeriodForSelection({
    selection: caDetailPrincipalSelection,
    fallbackStartDate: requestStartDate,
    fallbackEndDate: requestEndDate,
    fallbackAgeStoreIds: ageStoreIds,
    fallbackCohortUnit: cohortUnit,
    fallbackCohortValue: cohortValue,
  })
  const { data: caModalPrincipalNetworkKpis } = useNetworkDashboardKpis(
    caDetailPrincipalSelection?.storeIds ?? storeIds,
    caDetailPrincipalSelection?.storeAgeFilter ?? storeAgeFilter,
    caModalPrincipalPeriod,
  )
  const caModalNetworkKpis =
    caDetailPrincipalSelection !== null ? (caModalPrincipalNetworkKpis ?? networkKpis) : networkKpis

  const caModalComparableSelection = caDetailComparableSelection ?? mainComparableSelection
  const caModalComparablePeriod = buildNetworkKpisPeriodForSelection({
    selection: caModalComparableSelection,
    fallbackStartDate: requestStartDate,
    fallbackEndDate: requestEndDate,
    fallbackAgeStoreIds: ageStoreIds,
    fallbackCohortUnit: cohortUnit,
    fallbackCohortValue: cohortValue,
  })
  const { data: caModalComparableNetworkKpis } = useNetworkDashboardKpis(
    caModalComparableSelection?.storeIds ?? storeIds,
    caModalComparableSelection?.storeAgeFilter ?? storeAgeFilter,
    caModalComparablePeriod,
  )

  useEffect(() => {
    if (!isModalOpen && reopenCaModalAfterFiltersClose) {
      setReopenCaModalAfterFiltersClose(false)
      setIsCaModalOpen(true)
    }
  }, [isModalOpen, reopenCaModalAfterFiltersClose])

  const selectedDateRange = {
    from:
      requestStartDate === NETWORK_DASHBOARD_ALL_TIME_START_ISO
        ? null
        : DateTime.fromISO(requestStartDate).toJSDate(),
    to: DateTime.fromISO(requestEndDate).toJSDate(),
  }

  const principalRowSelection: NetworkFiltersRowSelection = {
    storeIds,
    storeAgeFilter,
    ageStoreIds,
    dateRange: selectedDateRange,
    cohort: {
      enabled: isCohortMode,
      unit: cohortUnit ?? 'week',
      value: cohortValue ?? 1,
    },
  }
  const comparableRowStartDate =
    comparableStartDate ??
    autoCohortComparableRange?.startDate ??
    networkKpis?.compStart ??
    requestStartDate
  const comparableRowEndDate =
    comparableEndDate ??
    autoCohortComparableRange?.endDate ??
    networkKpis?.compEnd ??
    requestEndDate
  const comparableRowSelection: NetworkFiltersRowSelection = {
    storeIds: comparableStoreIds,
    storeAgeFilter: comparableStoreAgeFilter,
    ageStoreIds: comparableAgeStoreIds,
    dateRange: {
      from:
        comparableRowStartDate === NETWORK_DASHBOARD_ALL_TIME_START_ISO
          ? null
          : DateTime.fromISO(comparableRowStartDate).toJSDate(),
      to: DateTime.fromISO(comparableRowEndDate).toJSDate(),
    },
    cohort: {
      enabled: isComparableCohortMode,
      unit: comparableCohortUnit ?? 'week',
      value: comparableCohortValue ?? 1,
    },
  }

  // Two effective "show > 3 mois" flags driven by the seniority filter:
  //  - `networkShowMatureOnly`: used for cards whose historical default was
  //    to aggregate over all stores (CA chart total). They only flip to the
  //    mature subset when the user explicitly picks `mature`.
  //  - `matureDefaultShowMatureOnly`: used for cards whose historical
  //    default was to aggregate over mature stores only (store CA, HR,
  //    network stat cards). They stay on the mature subset both for
  //    `default` and `mature`, and only switch to every store for `all`.
  const networkShowMatureOnly = false
  const matureDefaultShowMatureOnly = storeAgeFilter === 'default'

  const dailyAggregates = networkKpis?.dailyAggregates ?? []
  const caDailyAggregates = networkKpis?.caDailyAggregates ?? []
  const caModalDailyAggregates = caModalNetworkKpis?.dailyAggregates ?? []
  const caModalCaDailyAggregates = caModalNetworkKpis?.caDailyAggregates ?? []
  const mergedCaCardAggregates = mergeOperationalAndCaForCaCard(dailyAggregates, caDailyAggregates)
  const mergedCaModalAggregates = mergeOperationalAndCaForCaCard(
    caModalDailyAggregates,
    caModalCaDailyAggregates,
  )
  const caModalIntradayAggregates = caModalNetworkKpis?.cohortIntradayAggregates
  const mainComparisonUsesSeparateKpis =
    mainComparableSelection !== null && mainComparableNetworkKpis !== undefined
  const comparisonDailyAggregates = mainComparisonUsesSeparateKpis
    ? (mainComparableNetworkKpis?.dailyAggregates ?? [])
    : dailyAggregates
  const comparisonCaDailyAggregates = mainComparisonUsesSeparateKpis
    ? (mainComparableNetworkKpis?.caDailyAggregates ?? [])
    : caDailyAggregates
  const mergedMainComparisonAggregates = mergeOperationalAndCaForCaCard(
    comparisonDailyAggregates,
    comparisonCaDailyAggregates,
  )
  const modalComparableDailyAggregates =
    caModalComparableNetworkKpis?.dailyAggregates ?? dailyAggregates
  const modalComparableCaDailyAggregates =
    caModalComparableNetworkKpis?.caDailyAggregates ?? caDailyAggregates
  const mergedModalComparableAggregates = mergeOperationalAndCaForCaCard(
    modalComparableDailyAggregates,
    modalComparableCaDailyAggregates,
  )
  const showDashboardComparison = !isCohortMode || mainComparisonUsesSeparateKpis
  const showCaModalComparison =
    !isCohortMode ||
    (caModalComparableSelection !== null && caModalComparableNetworkKpis !== undefined)
  const categoryPdm = networkKpis?.categoryPdm ?? []
  const comparisonTopProductsMap = new Map(
    (mainComparisonUsesSeparateKpis
      ? (mainComparableNetworkKpis?.topProducts ?? [])
      : (networkKpis?.topProducts ?? [])
    ).map((product) => [product.reference, product.uvc]),
  )
  const topProducts = (networkKpis?.topProducts ?? []).map((product) => ({
    ...product,
    previousUvc: mainComparisonUsesSeparateKpis
      ? (comparisonTopProductsMap.get(product.reference) ?? 0)
      : product.previousUvc,
  }))
  const dashboardContextTitle = buildSelectionContextTitle({
    availableStores: networkKpis?.availableStores ?? [],
    storeIds,
    storeAgeFilter,
    ageStoreIds,
  })
  const networkCaCardContextTitle = dashboardContextTitle.startsWith('Réseau & Boutiques')
    ? dashboardContextTitle.replace('Réseau & Boutiques', 'CA Total')
    : `CA Total ${dashboardContextTitle}`
  const normalizedNetworkCaCardContextTitle =
    networkCaCardContextTitle === 'CA Total' ? 'CA Total Réseau' : networkCaCardContextTitle

  const periodBounds = networkKpis
    ? {
        periodStart: networkKpis.periodStart,
        periodEnd: networkKpis.periodEnd,
        compStart: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.periodStart ?? networkKpis.compStart)
          : networkKpis.compStart,
        compEnd: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.periodEnd ?? networkKpis.compEnd)
          : networkKpis.compEnd,
      }
    : null

  const caTotalsWindows: NetworkDashboardCACardDateWindows | undefined = networkKpis
    ? {
        periodStart: networkKpis.periodStart,
        periodEnd: networkKpis.periodEnd,
        compStart: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.periodStart ?? networkKpis.compStart)
          : networkKpis.compStart,
        compEnd: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.periodEnd ?? networkKpis.compEnd)
          : networkKpis.compEnd,
      }
    : undefined

  const caChartWindows: NetworkDashboardCACardDateWindows | undefined = networkKpis
    ? {
        periodStart: networkKpis.caChartMainStart,
        periodEnd: networkKpis.caChartMainEnd,
        compStart: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.caChartMainStart ?? networkKpis.caChartCompStart)
          : networkKpis.caChartCompStart,
        compEnd: mainComparisonUsesSeparateKpis
          ? (mainComparableNetworkKpis?.caChartMainEnd ?? networkKpis.caChartCompEnd)
          : networkKpis.caChartCompEnd,
      }
    : undefined
  const customComparisonTotalsWindows: NetworkDashboardCACardDateWindows | undefined =
    mainComparisonUsesSeparateKpis && mainComparableNetworkKpis
      ? {
          periodStart: mainComparableNetworkKpis.periodStart,
          periodEnd: mainComparableNetworkKpis.periodEnd,
          compStart: mainComparableNetworkKpis.compStart,
          compEnd: mainComparableNetworkKpis.compEnd,
        }
      : undefined
  const customComparisonChartWindows: NetworkDashboardCACardDateWindows | undefined =
    mainComparisonUsesSeparateKpis && mainComparableNetworkKpis
      ? {
          periodStart: mainComparableNetworkKpis.caChartMainStart,
          periodEnd: mainComparableNetworkKpis.caChartMainEnd,
          compStart: mainComparableNetworkKpis.caChartCompStart,
          compEnd: mainComparableNetworkKpis.caChartCompEnd,
        }
      : undefined
  const comparableCaTotalsWindows: NetworkDashboardCACardDateWindows | undefined =
    caModalComparableNetworkKpis
      ? {
          periodStart: caModalComparableNetworkKpis.periodStart,
          periodEnd: caModalComparableNetworkKpis.periodEnd,
          compStart: caModalComparableNetworkKpis.compStart,
          compEnd: caModalComparableNetworkKpis.compEnd,
        }
      : caTotalsWindows
  const comparableCaChartWindows: NetworkDashboardCACardDateWindows | undefined =
    caModalComparableNetworkKpis
      ? {
          periodStart: caModalComparableNetworkKpis.caChartMainStart,
          periodEnd: caModalComparableNetworkKpis.caChartMainEnd,
          compStart: caModalComparableNetworkKpis.caChartCompStart,
          compEnd: caModalComparableNetworkKpis.caChartCompEnd,
        }
      : caChartWindows
  const caModalTotalsWindows: NetworkDashboardCACardDateWindows | undefined = caModalNetworkKpis
    ? {
        periodStart: caModalNetworkKpis.periodStart,
        periodEnd: caModalNetworkKpis.periodEnd,
        compStart: caModalNetworkKpis.compStart,
        compEnd: caModalNetworkKpis.compEnd,
      }
    : caTotalsWindows
  const caModalChartWindows: NetworkDashboardCACardDateWindows | undefined = caModalNetworkKpis
    ? {
        periodStart: caModalNetworkKpis.caChartMainStart,
        periodEnd: caModalNetworkKpis.caChartMainEnd,
        compStart: caModalNetworkKpis.caChartCompStart,
        compEnd: caModalNetworkKpis.caChartCompEnd,
      }
    : caChartWindows

  let comparisonLabel = 'vs S-1'
  let caChartLegend: { period: string; comparison: string } | undefined = undefined
  if (networkKpis) {
    const comparisonStart = mainComparisonUsesSeparateKpis
      ? (mainComparableNetworkKpis?.periodStart ?? networkKpis.compStart)
      : networkKpis.compStart
    const comparisonEnd = mainComparisonUsesSeparateKpis
      ? (mainComparableNetworkKpis?.periodEnd ?? networkKpis.compEnd)
      : networkKpis.compEnd
    const formattedMainPeriod = formatNetworkDashboardComparisonPeriodFr(
      networkKpis.periodStart,
      networkKpis.periodEnd,
    )
    const formattedComparisonPeriod = formatNetworkDashboardComparisonPeriodFr(
      comparisonStart,
      comparisonEnd,
    )

    if (mainComparisonUsesSeparateKpis) {
      comparisonLabel = `vs ${formattedComparisonPeriod}`
      caChartLegend = {
        period: `Période (${formattedMainPeriod})`,
        comparison: `Comparaison (${formattedComparisonPeriod})`,
      }
    } else {
      comparisonLabel = `vs ${formattedComparisonPeriod}-1`
      caChartLegend = {
        period: `Période (${formattedMainPeriod})`,
        comparison: `Comparaison (${formattedComparisonPeriod}-1)`,
      }
    }
  }

  const currentWeekAggregates = periodBounds
    ? filterAggregatesInRange(dailyAggregates, periodBounds.periodStart, periodBounds.periodEnd)
    : []
  const previousWeekAggregates = periodBounds
    ? filterAggregatesInRange(
        comparisonDailyAggregates,
        periodBounds.compStart,
        periodBounds.compEnd,
      )
    : []
  const currentCaRows =
    periodBounds != null
      ? filterCaAggregatesInRange(
          caDailyAggregates,
          periodBounds.periodStart,
          periodBounds.periodEnd,
        )
      : []
  const previousCaRows =
    periodBounds != null
      ? filterCaAggregatesInRange(
          comparisonCaDailyAggregates,
          periodBounds.compStart,
          periodBounds.compEnd,
        )
      : []

  // The following block feeds the cards whose pre-filter default was mature
  // only (store CA, HR, stat cards). `matureDefaultShowMatureOnly` is true
  // when the override is `default` or `mature`, false only when `all`.
  const currentCaTTC = sumCaRows(currentCaRows, matureDefaultShowMatureOnly)
  const previousCaTTCSelected = sumCaRows(previousCaRows, matureDefaultShowMatureOnly)
  const currentCartsCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const previousCartsCount = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const currentProductsSold = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureProductsSold', 'totalProductsSold'),
    0,
  )

  const currentWeekMerged =
    periodBounds != null
      ? mergedCaCardAggregates.filter(
          (a) => a.date >= periodBounds.periodStart && a.date <= periodBounds.periodEnd,
        )
      : []

  const currentCAPerStore = currentWeekMerged.reduce((sum, a) => {
    const storeCount = matureDefaultShowMatureOnly ? a.matureStoreCount : a.storeCount
    const caValue = matureDefaultShowMatureOnly ? a.matureCa : a.totalCa
    if (storeCount <= 0) return sum
    return sum + caValue / storeCount
  }, 0)

  const hrMetrics = networkKpis?.hrMetrics
  const hrPercentageCA =
    hrMetrics && currentCAPerStore > 0
      ? Math.round((hrMetrics.avgSalaryCostPerStore / currentCAPerStore) * 100)
      : 0

  const panierMoyen = currentCartsCount === 0 ? 0 : currentCaTTC / currentCartsCount
  const previousPanierMoyen =
    previousCartsCount === 0 ? 0 : previousCaTTCSelected / previousCartsCount
  const panierMoyenPercentageChange =
    previousPanierMoyen === 0
      ? 0
      : Math.round(((panierMoyen - previousPanierMoyen) / previousPanierMoyen) * 100)

  const articlesPerCart = currentCartsCount === 0 ? 0 : currentProductsSold / currentCartsCount

  const currentCaHT = currentWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )
  const previousCaHT = previousWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )

  const currentStolenValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const previousStolenValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const currentStolenCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureStolenCount', 'totalStolenCount'),
    0,
  )
  const txVol = currentCaHT > 0 ? (currentStolenValue / currentCaHT) * 100 : 0
  const previousTxVol = previousCaHT > 0 ? (previousStolenValue / previousCaHT) * 100 : 0
  const txVolChange =
    previousTxVol === 0 ? 0 : Math.round(((txVol - previousTxVol) / previousTxVol) * 100)

  const currentDestroyedValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const previousDestroyedValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const currentDestroyedCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedCount',
        'totalDestroyedCount',
      ),
    0,
  )
  const txCasse = currentCaHT > 0 ? (currentDestroyedValue / currentCaHT) * 100 : 0
  const previousTxCasse = previousCaHT > 0 ? (previousDestroyedValue / previousCaHT) * 100 : 0
  const txCasseChange =
    previousTxCasse === 0 ? 0 : Math.round(((txCasse - previousTxCasse) / previousTxCasse) * 100)

  const delta = realEstateKpis?.openStoresDelta ?? 0
  const openStoresDeltaIndex = `${delta >= 0 ? '+' : ''}${delta} vs S-1`
  const typedProjects = projects as RealEstateProject[]
  const openStoresCount = typedProjects.filter((p) => p.status === 'open' && !p.isAbandoned).length
  const storesAvailableForCharges = networkKpis?.availableStores ?? []
  const otherChargesMainEnd = periodBounds?.periodEnd ?? requestEndDate
  const otherChargesComparisonEnd = periodBounds?.compEnd ?? requestEndDate
  const selectedStoresForOtherCharges = resolveSelectedStoresForPeriod({
    availableStores: storesAvailableForCharges,
    storeIds,
    storeAgeFilter,
    ageStoreIds,
    periodEnd: otherChargesMainEnd,
  })
  const comparableStoresForOtherCharges =
    mainComparisonUsesSeparateKpis && mainComparableSelection !== null
      ? resolveSelectedStoresForPeriod({
          availableStores: storesAvailableForCharges,
          storeIds: mainComparableSelection.storeIds,
          storeAgeFilter: mainComparableSelection.storeAgeFilter,
          ageStoreIds: mainComparableSelection.ageStoreIds,
          periodEnd: otherChargesComparisonEnd,
        })
      : resolveSelectedStoresForPeriod({
          availableStores: storesAvailableForCharges,
          storeIds,
          storeAgeFilter,
          ageStoreIds,
          periodEnd: otherChargesComparisonEnd,
        })
  const otherChargesOpenStoresCount = countOpenStoresAtPeriodEnd(
    selectedStoresForOtherCharges,
    otherChargesMainEnd,
  )
  const otherChargesComparisonOpenStoresCount = countOpenStoresAtPeriodEnd(
    comparableStoresForOtherCharges,
    otherChargesComparisonEnd,
  )
  const otherCharges = otherChargesOpenStoresCount * 15_000
  const otherChargesComparison = otherChargesComparisonOpenStoresCount * 15_000
  const totalCaAnnualized = periodBounds
    ? annualizePeriodValue(
        sumCaRows(currentCaRows, false),
        periodBounds.periodStart,
        periodBounds.periodEnd,
      )
    : 0
  const comparisonTotalCaAnnualized = periodBounds
    ? annualizePeriodValue(
        sumCaRows(previousCaRows, false),
        periodBounds.compStart,
        periodBounds.compEnd,
      )
    : 0
  const otherChargesPercentOfAnnualizedCa =
    totalCaAnnualized > 0 ? (otherCharges / totalCaAnnualized) * 100 : 0
  const otherChargesComparisonPercentOfAnnualizedCa =
    comparisonTotalCaAnnualized > 0
      ? (otherChargesComparison / comparisonTotalCaAnnualized) * 100
      : 0
  const otherChargesChange = computePercentageChange(
    otherChargesPercentOfAnnualizedCa,
    otherChargesComparisonPercentOfAnnualizedCa,
  )
  const today = new Date()

  const upcomingStores = getUpcomingStores(typedProjects).filter(
    (p) =>
      today <= p.openingDate &&
      p.openingDate <= new Date(today.getTime() + 3 * 3_600_000 * 24 * 30),
  )

  const avgLandCostEur = realEstateKpis?.avgLandCostEur ?? computeAverageLandCost(typedProjects)
  const avgRent = computeAverageRent(typedProjects)
  const avgLandCostPctOfCa = getPercentageOfAnnualCa(
    avgLandCostEur,
    realEstateKpis?.averageAnnualCaEur ?? 0,
  )
  const avgRentPctOfCa = getPercentageOfAnnualCa(avgRent, realEstateKpis?.averageAnnualCaEur ?? 0)

  const liveCaDetailsTotal = liveStoreCa?.networkTotalCa ?? 0
  const liveCaDetailsComparison = liveStoreCa?.networkYesterdayCa ?? 0
  let liveCaDetailsPercentageChange = 0
  if (liveCaDetailsComparison !== 0) {
    liveCaDetailsPercentageChange = Math.round(
      ((liveCaDetailsTotal - liveCaDetailsComparison) / liveCaDetailsComparison) * 100,
    )
  }

  const caModalStoreAgeFilter = caDetailPrincipalSelection?.storeAgeFilter ?? storeAgeFilter
  const caModalShowMatureOnly = resolveCaModalShowMatureOnly(caModalVariant, caModalStoreAgeFilter)
  const caModalComparableStoreAgeFilter =
    caModalComparableSelection?.storeAgeFilter ?? storeAgeFilter
  const caModalComparableShowMatureOnly = resolveCaModalShowMatureOnly(
    caModalVariant,
    caModalComparableStoreAgeFilter,
  )
  const caModalPrincipalIndicatorId = caModalVariant === 'store' ? 'ca-moyen' : 'ca'
  const caModalHasLocalPrincipalSelection = caDetailPrincipalSelection !== null
  const caModalIsCohortMode = caModalHasLocalPrincipalSelection
    ? caDetailPrincipalSelection.cohort?.enabled === true
    : isCohortMode
  const caModalCohortLabel = caModalHasLocalPrincipalSelection
    ? formatCohortBadgeLabel(
        caDetailPrincipalSelection.cohort?.unit,
        caDetailPrincipalSelection.cohort?.value,
      )
    : formatCohortBadgeLabel(cohortUnit, cohortValue)
  const caModalStartDate =
    caModalHasLocalPrincipalSelection && !caModalIsCohortMode
      ? caDetailPrincipalSelection.dateRange.from
        ? DateTime.fromJSDate(caDetailPrincipalSelection.dateRange.from).toISODate()!
        : NETWORK_DASHBOARD_ALL_TIME_START_ISO
      : requestStartDate
  const caModalEndDate =
    caModalHasLocalPrincipalSelection && !caModalIsCohortMode
      ? DateTime.fromJSDate(caDetailPrincipalSelection.dateRange.to).toISODate()!
      : requestEndDate

  return (
    <>
      <AppHeader className="mb-4">
        <div className="flex items-center gap-2">
          <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
            {dashboardContextTitle}
          </Title>
          <SelectionPeriodBadge
            startDate={requestStartDate}
            endDate={requestEndDate}
            label={isCohortMode ? formatCohortBadgeLabel(cohortUnit, cohortValue) : undefined}
          />
        </div>
      </AppHeader>

      <AppContainer>
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="w-[180px] flex items-center justify-between"
            type="button"
            onClick={() => {
              setReopenCaModalAfterFiltersClose(false)
              setSelectorSource('header')
              setIsModalOpen(true)
            }}
          >
            Sélecteur
            <ChevronDownIcon className="w-4 h-4" />
          </Button>
        </div>

        {isModalOpen && (
          <NetworkFilters
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            comparisonMode="dual"
            principalIndicator={
              selectorSource === 'caModal'
                ? {
                    ...CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS,
                    defaultSelectedId: caModalPrincipalIndicatorId,
                  }
                : undefined
            }
            comparableIndicator={
              selectorSource === 'caModal'
                ? {
                    ...CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS,
                    defaultSelectedId:
                      caDetailIndicatorIds?.comparable ??
                      CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS.defaultSelectedId,
                  }
                : undefined
            }
            principal={
              selectorSource === 'caModal'
                ? (caDetailPrincipalSelection ?? principalRowSelection)
                : principalRowSelection
            }
            comparable={
              selectorSource === 'caModal'
                ? (caDetailComparableSelection ?? comparableRowSelection)
                : comparableRowSelection
            }
            showComparableModeSwitch={selectorSource === 'header'}
            initialComparableSelectionMode={
              selectorSource === 'header' ? effectiveComparableSelectionMode : 'custom'
            }
            availableStores={networkKpis?.availableStores ?? []}
            onApply={(payload) => {
              if (payload.comparisonMode === 'dual') {
                if (selectorSource === 'caModal') {
                  const nextPrincipalIndicatorId = payload.principal.indicatorId ?? 'ca'
                  setCaDetailIndicatorIds({
                    principal: nextPrincipalIndicatorId,
                    comparable: payload.comparable.indicatorId ?? 'uc',
                  })
                  setCaModalVariant(nextPrincipalIndicatorId === 'ca-moyen' ? 'store' : 'network')
                  setCaDetailPrincipalSelection({
                    storeIds: payload.principal.storeIds,
                    storeAgeFilter: payload.principal.storeAgeFilter,
                    ageStoreIds: payload.principal.ageStoreIds,
                    dateRange: payload.principal.dateRange,
                    cohort: payload.principal.cohort,
                  })
                  setCaDetailComparableSelection({
                    storeIds: payload.comparable.storeIds,
                    storeAgeFilter: payload.comparable.storeAgeFilter,
                    ageStoreIds: payload.comparable.ageStoreIds,
                    dateRange: payload.comparable.dateRange,
                    cohort: payload.comparable.cohort,
                  })
                  setIsModalOpen(false)
                  return
                } else {
                  setCaDetailPrincipalSelection(null)
                  setCaDetailComparableSelection(null)
                }
                const p = payload.principal
                const c = payload.comparable
                const isCustomComparable = payload.comparableSelectionMode === 'custom'
                const shouldUseCohort = selectorSource === 'header' && p.cohort?.enabled === true
                const shouldUseComparableCohort = isCustomComparable && c.cohort?.enabled === true
                let comparableStartDateForSearch: string | undefined = undefined
                let comparableEndDateForSearch: string | undefined = undefined
                if (isCustomComparable && !shouldUseComparableCohort) {
                  comparableStartDateForSearch = c.dateRange.from
                    ? DateTime.fromJSDate(c.dateRange.from).toISODate()!
                    : NETWORK_DASHBOARD_ALL_TIME_START_ISO
                  comparableEndDateForSearch = DateTime.fromJSDate(c.dateRange.to).toISODate()!
                }
                let principalStartDateForSearch: string | undefined = undefined
                let principalEndDateForSearch: string | undefined = undefined
                if (!shouldUseCohort) {
                  principalStartDateForSearch = p.dateRange.from
                    ? DateTime.fromJSDate(p.dateRange.from).toISODate()!
                    : NETWORK_DASHBOARD_ALL_TIME_START_ISO
                  principalEndDateForSearch = DateTime.fromJSDate(p.dateRange.to).toISODate()!
                }
                void navigate({
                  search: {
                    storeIds: p.storeIds.length > 0 ? p.storeIds : undefined,
                    storeAgeFilter: p.storeAgeFilter === 'default' ? undefined : p.storeAgeFilter,
                    ageStoreIds:
                      p.ageStoreIds !== undefined && p.ageStoreIds.length > 0
                        ? p.ageStoreIds
                        : undefined,
                    startDate: principalStartDateForSearch,
                    endDate: principalEndDateForSearch,
                    cohortUnit: shouldUseCohort ? p.cohort?.unit : undefined,
                    cohortValue: shouldUseCohort ? p.cohort?.value : undefined,
                    comparableSelectionMode: payload.comparableSelectionMode,
                    comparableStoreIds:
                      isCustomComparable && c.storeIds.length > 0 ? c.storeIds : undefined,
                    comparableStoreAgeFilter:
                      isCustomComparable && c.storeAgeFilter !== 'default'
                        ? c.storeAgeFilter
                        : undefined,
                    comparableAgeStoreIds:
                      isCustomComparable && c.ageStoreIds !== undefined && c.ageStoreIds.length > 0
                        ? c.ageStoreIds
                        : undefined,
                    comparableStartDate: comparableStartDateForSearch,
                    comparableEndDate: comparableEndDateForSearch,
                    comparableCohortUnit: shouldUseComparableCohort ? c.cohort?.unit : undefined,
                    comparableCohortValue: shouldUseComparableCohort ? c.cohort?.value : undefined,
                  },
                })
                setIsModalOpen(false)
                return
              }
              const {
                cohort,
                dateRange,
                storeAgeFilter: nextAge,
                storeIds: nextIds,
                ageStoreIds: nextAgeIds,
              } = payload
              let startDateForSearch: string | undefined = undefined
              let endDateForSearch: string | undefined = undefined
              if (cohort?.enabled !== true) {
                startDateForSearch = dateRange.from
                  ? DateTime.fromJSDate(dateRange.from).toISODate()!
                  : NETWORK_DASHBOARD_ALL_TIME_START_ISO
                endDateForSearch = DateTime.fromJSDate(dateRange.to).toISODate()!
              }
              void navigate({
                search: {
                  storeIds: nextIds.length > 0 ? nextIds : undefined,
                  storeAgeFilter: nextAge === 'default' ? undefined : nextAge,
                  ageStoreIds:
                    nextAgeIds !== undefined && nextAgeIds.length > 0 ? nextAgeIds : undefined,
                  startDate: startDateForSearch,
                  endDate: endDateForSearch,
                  cohortUnit: cohort?.enabled === true ? cohort.unit : undefined,
                  cohortValue: cohort?.enabled === true ? cohort.value : undefined,
                  comparableSelectionMode: undefined,
                  comparableStoreIds: undefined,
                  comparableStoreAgeFilter: undefined,
                  comparableAgeStoreIds: undefined,
                  comparableStartDate: undefined,
                  comparableEndDate: undefined,
                  comparableCohortUnit: undefined,
                  comparableCohortValue: undefined,
                },
              })
              setIsModalOpen(false)
            }}
          />
        )}

        <CaModal
          open={isCaModalOpen}
          onOpenChange={setIsCaModalOpen}
          contextTitle={
            caModalVariant === 'network' ? normalizedNetworkCaCardContextTitle : undefined
          }
          startDate={caModalStartDate}
          endDate={caModalEndDate}
          variant={caModalVariant}
          aggregates={mergedCaModalAggregates}
          intradayAggregates={caModalIntradayAggregates}
          comparableAggregates={mergedModalComparableAggregates}
          showMatureOnly={caModalShowMatureOnly}
          comparableShowMatureOnly={caModalComparableShowMatureOnly}
          storeAgeFilter={caModalStoreAgeFilter}
          isStoreSelectionActive={(caDetailPrincipalSelection?.storeIds ?? storeIds).length > 0}
          totalsWindows={caModalTotalsWindows}
          chartWindows={caModalChartWindows}
          comparableTotalsWindows={comparableCaTotalsWindows}
          comparableChartWindows={comparableCaChartWindows}
          caChartGranularity={caModalNetworkKpis?.caChartGranularity ?? 'day'}
          mainIndicatorId={caModalPrincipalIndicatorId}
          comparableIndicatorId={
            caDetailIndicatorIds?.comparable ??
            CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS.defaultSelectedId ??
            'uc'
          }
          comparisonText={comparisonLabel}
          showComparison={showCaModalComparison}
          isCohortMode={caModalIsCohortMode}
          cohortLabel={caModalCohortLabel}
          onOpenPeriodSelector={() => {
            setReopenCaModalAfterFiltersClose(false)
            setSelectorSource('caModal')
            setIsModalOpen(true)
          }}
        />

        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: '16rem minmax(0, 1fr) minmax(10rem, 15rem)' }}
        >
          {/* Left column */}
          <div className="max-h-0 min-h-full flex flex-col gap-2">
            <OpenStoresCountCardLink
              openStoresCount={openStoresCount}
              openStoresDeltaIndex={openStoresDeltaIndex}
            />
            <UpcomingStoresCard upcomingStores={upcomingStores} />
            <RealEstateCostsCard
              avgLandCostEur={avgLandCostEur ?? 0}
              avgLandCostPctOfCa={avgLandCostPctOfCa ?? ''}
              avgRent={avgRent ?? 0}
              avgRentPctOfCa={avgRentPctOfCa ?? ''}
            />
            <CustomerSatifsactionCard customerSatifsaction={4.6} />
          </div>

          {/* Center column */}
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 gap-2">
              <div className="flex-1 min-w-0">
                <CACard
                  variant="network"
                  aggregates={mergedCaCardAggregates}
                  weekdayAverageAggregates={networkKpis?.cohortIntradayAggregates}
                  comparisonAggregates={
                    mainComparisonUsesSeparateKpis ? mergedMainComparisonAggregates : undefined
                  }
                  weekdayAverageComparisonAggregates={
                    mainComparisonUsesSeparateKpis
                      ? (mainComparableNetworkKpis?.cohortIntradayAggregates ??
                        mergedMainComparisonAggregates)
                      : undefined
                  }
                  showMatureOnly={networkShowMatureOnly}
                  storeAgeFilter={storeAgeFilter}
                  isStoreSelectionActive={storeIds.length > 0}
                  selectionContextTitle={normalizedNetworkCaCardContextTitle}
                  totalsWindows={caTotalsWindows}
                  chartWindows={caChartWindows}
                  comparisonTotalsWindows={customComparisonTotalsWindows}
                  comparisonChartWindows={customComparisonChartWindows}
                  caChartGranularity={networkKpis?.caChartGranularity ?? 'day'}
                  comparisonText={comparisonLabel}
                  chartLegend={caChartLegend}
                  showComparison={showDashboardComparison}
                  onClick={() => {
                    setCaModalVariant('network')
                    setIsCaModalOpen(true)
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <CACard
                  variant="store"
                  aggregates={mergedCaCardAggregates}
                  weekdayAverageAggregates={networkKpis?.cohortIntradayAggregates}
                  comparisonAggregates={
                    mainComparisonUsesSeparateKpis ? mergedMainComparisonAggregates : undefined
                  }
                  weekdayAverageComparisonAggregates={
                    mainComparisonUsesSeparateKpis
                      ? (mainComparableNetworkKpis?.cohortIntradayAggregates ??
                        mergedMainComparisonAggregates)
                      : undefined
                  }
                  showMatureOnly={matureDefaultShowMatureOnly}
                  storeAgeFilter={storeAgeFilter}
                  selectionContextTitle={dashboardContextTitle}
                  totalsWindows={caTotalsWindows}
                  chartWindows={caChartWindows}
                  comparisonTotalsWindows={customComparisonTotalsWindows}
                  comparisonChartWindows={customComparisonChartWindows}
                  caChartGranularity={networkKpis?.caChartGranularity ?? 'day'}
                  comparisonText={comparisonLabel}
                  chartLegend={caChartLegend}
                  showComparison={showDashboardComparison}
                  onClick={() => {
                    setCaModalVariant('store')
                    setIsCaModalOpen(true)
                  }}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex min-h-0 min-w-0 flex-[1.2] flex-col">
                <CategoriesCard categories={categoryPdm} storeAgeFilter={storeAgeFilter} />
              </div>
              <div className="flex min-h-0 flex-[0.8] flex-col min-w-0">
                <ProductsCard
                  products={topProducts}
                  categories={categoryPdm}
                  storeAgeFilter={storeAgeFilter}
                  comparisonLabel={comparisonLabel}
                  showComparison={showDashboardComparison}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-[0.4] flex">
                <HRCard
                  avgETP={hrMetrics?.avgETP ?? 0}
                  percentageCA={hrPercentageCA}
                  percentageInPerson={hrMetrics?.inPersonHoursTotal ?? 0}
                  percentageRemote={hrMetrics?.remoteHoursTotal ?? 0}
                  storeAgeFilter={storeAgeFilter}
                />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                <NetworkStatCard
                  title="Tx de casse"
                  value={`${txCasse.toFixed(1)}%`}
                  subText={`${Math.round(currentDestroyedCount)} UVC`}
                  comparisonPercentage={txCasseChange}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
                <NetworkStatCard
                  title="Tx de vol"
                  value={`${txVol.toFixed(1)}%`}
                  subText={`${Math.round(currentStolenCount)} UVC`}
                  comparisonPercentage={txVolChange}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
                <NetworkStatCard
                  title="Autres charges"
                  value={formatPercentValue(otherChargesPercentOfAnnualizedCa)}
                  subText={`${otherCharges.toLocaleString('fr-FR')} €`}
                  comparisonPercentage={otherChargesChange}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
                <NetworkStatCard
                  title="Tx de service"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
                <NetworkStatCard
                  title="Panier moyen"
                  value={`${Number(panierMoyen.toFixed(2)).toLocaleString('fr-FR')} €`}
                  subText={`${articlesPerCart.toFixed(1)} A/T`}
                  comparisonPercentage={panierMoyenPercentageChange}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
                <NetworkStatCard
                  variant="primary-light"
                  title="Contribution"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={showDashboardComparison}
                />
              </div>
            </div>
          </div>

          {/* Right column — max-h-0 + min-h-full prevents this cell from
             influencing the grid row height while still stretching to fill it */}
          <div className="max-h-0 min-h-full">
            <CADetailsCard
              caTTC={liveCaDetailsTotal}
              caTTCPercentageChange={liveCaDetailsPercentageChange}
              storeWeeklyCas={liveStoreCa?.storeCas ?? []}
              storeAgeFilter="all"
              comparisonText="vs J-1"
            />
          </div>
        </div>
      </AppContainer>
    </>
  )
}

const networkDashboardSearchSchema = z
  .object({
    storeIds: z.array(z.number()).optional().default([]),
    storeAgeFilter: z.enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS).optional().default('default'),
    ageStoreIds: z.array(z.number()).optional(),
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    cohortUnit: z.enum(NETWORK_DASHBOARD_COHORT_UNITS).optional(),
    cohortValue: z.number().int().positive().optional(),
    comparableSelectionMode: z.enum(['auto', 'custom']).optional().default('auto'),
    comparableStoreIds: z.array(z.number()).optional().default([]),
    comparableStoreAgeFilter: z
      .enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS)
      .optional()
      .default('default'),
    comparableAgeStoreIds: z.array(z.number()).optional(),
    comparableStartDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    comparableEndDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    comparableCohortUnit: z.enum(NETWORK_DASHBOARD_COHORT_UNITS).optional(),
    comparableCohortValue: z.number().int().positive().optional(),
  })
  .refine(
    (data) =>
      (data.startDate === undefined && data.endDate === undefined) ||
      (data.startDate !== undefined && data.endDate !== undefined),
    { message: 'startDate and endDate must both be set or both omitted', path: ['startDate'] },
  )
  .refine(
    (data) =>
      (data.cohortUnit === undefined && data.cohortValue === undefined) ||
      (data.cohortUnit !== undefined && data.cohortValue !== undefined),
    {
      message: 'cohortUnit and cohortValue must both be set or both omitted',
      path: ['cohortUnit'],
    },
  )
  .refine((data) => data.cohortUnit === undefined || data.startDate === undefined, {
    message: 'cohort params cannot be combined with date params',
    path: ['cohortUnit'],
  })
  .refine(
    (data) =>
      (data.comparableStartDate === undefined && data.comparableEndDate === undefined) ||
      (data.comparableStartDate !== undefined && data.comparableEndDate !== undefined),
    {
      message: 'comparableStartDate and comparableEndDate must both be set or both omitted',
      path: ['comparableStartDate'],
    },
  )
  .refine(
    (data) =>
      data.comparableSelectionMode === 'auto' ||
      (data.comparableStartDate !== undefined && data.comparableEndDate !== undefined) ||
      (data.comparableCohortUnit !== undefined && data.comparableCohortValue !== undefined),
    {
      message:
        'comparable date or cohort params are required when comparableSelectionMode is custom',
      path: ['comparableSelectionMode'],
    },
  )
  .refine(
    (data) =>
      (data.comparableCohortUnit === undefined && data.comparableCohortValue === undefined) ||
      (data.comparableCohortUnit !== undefined && data.comparableCohortValue !== undefined),
    {
      message: 'comparableCohortUnit and comparableCohortValue must both be set or both omitted',
      path: ['comparableCohortUnit'],
    },
  )
  .refine(
    (data) => data.comparableCohortUnit === undefined || data.comparableStartDate === undefined,
    {
      message: 'comparable cohort params cannot be combined with comparable date params',
      path: ['comparableCohortUnit'],
    },
  )
  .refine(
    (data) => {
      if (data.cohortUnit === undefined || data.cohortValue === undefined) {
        return true
      }
      const limits: Record<NetworkDashboardCohortUnit, { min: number; max: number }> = {
        day: { min: 1, max: 90 },
        week: { min: 1, max: 24 },
        month: { min: 1, max: 24 },
        year: { min: 1, max: 5 },
      }
      const limit = limits[data.cohortUnit]
      return data.cohortValue >= limit.min && data.cohortValue <= limit.max
    },
    { message: 'cohortValue is out of range for cohortUnit', path: ['cohortValue'] },
  )
  .refine(
    (data) => {
      if (data.comparableCohortUnit === undefined || data.comparableCohortValue === undefined) {
        return true
      }
      const limits: Record<NetworkDashboardCohortUnit, { min: number; max: number }> = {
        day: { min: 1, max: 90 },
        week: { min: 1, max: 24 },
        month: { min: 1, max: 24 },
        year: { min: 1, max: 5 },
      }
      const limit = limits[data.comparableCohortUnit]
      return data.comparableCohortValue >= limit.min && data.comparableCohortValue <= limit.max
    },
    {
      message: 'comparableCohortValue is out of range for comparableCohortUnit',
      path: ['comparableCohortValue'],
    },
  )

export type NetworkDashboardSearch = z.infer<typeof networkDashboardSearchSchema>

export const Route = createFileRoute('/_authenticated/network-dashboard/')({
  component: NetworkDashboardPage,
  validateSearch: networkDashboardSearchSchema,
})
